import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, Armchair, Flag, LogIn, LogOut, Play, Plus, Trash2 } from "lucide-react";
import { resolveClockTime, toClockInput } from "../../timeline";
import "./timeline-editor.css";

const TYPE_LABELS = { buyin: "Sat down", rebuy: "Rebuy", out: "Out", back: "Back in" };
const TYPE_ICONS = { buyin: Armchair, rebuy: Plus, out: LogOut, back: LogIn };
// Same-minute entries read in the order they happen at a table.
const TYPE_ORDER = { buyin: 0, back: 1, rebuy: 2, out: 3 };

const newId = () => Math.random().toString(36).slice(2, 10);
// Inputs work in whole minutes, so checks do too: a rebuy typed as "21:40"
// is not "before" a seat taken at 21:40:35.
const minute = (t) => Math.floor(t / 60000);
const byTime = (a, b) => (minute(a.t) - minute(b.t)) || (TYPE_ORDER[a.type] - TYPE_ORDER[b.type]) || (a.t - b.t);
const rowsKey = (list) => list.map((r) => `${r.id}:${r.t}`).sort().join("|");

function formatLength(ms) {
  const mins = Math.max(0, Math.round(ms / 60000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (!h) return `${m} min`;
  return m ? `${h} h ${m} min` : `${h} h`;
}

// A compact iOS-style time chip. 16px text so iOS Safari doesn't zoom.
function TimeChip({ id, value, onChange, invalid, label, inputRef }) {
  return (
    <input
      id={id}
      ref={inputRef}
      className="tl-time"
      type="time"
      aria-label={label}
      aria-invalid={invalid || undefined}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

// Full-page editor for a game's timeline: start (and end, for a saved game)
// and each buy-in, rebuy and bust, grouped by player. Works on a draft;
// onSave gets the result plus an optional reason, and the caller writes it.
// Seats can be moved but not removed, since a seat is what puts a player in
// the game.
export const TimelineEditor = memo(function TimelineEditor({ live = false, startedAt, endedAt, events, playerIds, getName, renderAvatar, onSave, onClose }) {
  const [start, setStart] = useState(startedAt);
  const [end, setEnd] = useState(endedAt || null);
  // anchor: the time each row opened with, so "00:30" resolves to the right day.
  const [rows, setRows] = useState(() => events.map((ev) => ({ ...ev, anchor: ev.t })));
  const [reason, setReason] = useState("");
  const [error, setError] = useState(null); // { message, id }
  const [saving, setSaving] = useState(false);
  const [addedId, setAddedId] = useState(null);
  const contentRef = useRef(null);
  const addedRef = useRef(null);
  const initialKey = useMemo(() => rowsKey(events), [events]);

  const sorted = useMemo(() => [...rows].sort(byTime), [rows]);

  // Players in table order, plus anyone with entries who has since left the list.
  const players = useMemo(() => {
    const ids = [...playerIds];
    for (const r of rows) if (!ids.includes(r.pid)) ids.push(r.pid);
    return ids;
  }, [playerIds, rows]);

  const dirty = start !== startedAt
    || (!live && end !== (endedAt || null))
    || reason.trim() !== ""
    || rowsKey(rows) !== initialKey;

  // A freshly added entry gets focus, so its time can be set straight away.
  useEffect(() => {
    if (addedId && addedRef.current) addedRef.current.focus();
  }, [addedId]);

  const clearError = () => setError(null);

  const changeStart = (value) => {
    const next = resolveClockTime(value, startedAt);
    if (next === null) return;
    // Seats taken when the game opened move with the start.
    setRows((list) => list.map((r) => (r.type === "buyin" && r.t === start ? { ...r, t: next } : r)));
    setStart(next);
    clearError();
  };
  const changeEnd = (value) => {
    const next = resolveClockTime(value, endedAt);
    if (next !== null) { setEnd(next); clearError(); }
  };
  const changeRow = (id, value) => {
    setRows((list) => list.map((r) => {
      if (r.id !== id || value === toClockInput(r.t)) return r;
      const t = resolveClockTime(value, r.anchor);
      return t === null ? r : { ...r, t };
    }));
    clearError();
  };
  const removeRow = (id) => { setRows((list) => list.filter((r) => r.id !== id)); clearError(); };
  // New entries start at now (live) or at the end of the night (saved), the
  // likeliest spot for a missed tap; the time chip is focused to adjust it.
  const addRow = (pid, type) => {
    const t = live ? Date.now() : end;
    const id = newId();
    setRows((list) => [...list, { id, type, pid, t, anchor: t }]);
    setAddedId(id);
    clearError();
  };

  const seatTimes = () => {
    const seatOf = {};
    for (const r of sorted) if (r.type === "buyin" && seatOf[r.pid] === undefined) seatOf[r.pid] = r.t;
    return seatOf;
  };

  function validate() {
    const limit = live ? Date.now() : end;
    if (!live && minute(end) < minute(start)) return { message: "The end time can't be before the start time.", id: "end" };
    const seatOf = seatTimes();
    for (const r of sorted) {
      const name = getName(r.pid) || "A player";
      const what = TYPE_LABELS[r.type].toLowerCase();
      if (minute(r.t) < minute(start)) return { message: `${name}: ${what} is before the game started.`, id: r.id };
      if (minute(r.t) > minute(limit)) return { message: `${name}: ${what} is ${live ? "in the future" : "after the game ended"}.`, id: r.id };
      if (r.type !== "buyin" && seatOf[r.pid] !== undefined && minute(r.t) < minute(seatOf[r.pid])) return { message: `${name}: ${what} is before they sat down.`, id: r.id };
    }
    return null;
  }

  // Within the allowed minute, keep the order right: nothing before the
  // start or the player's seat, nothing after the end (or now).
  function clampedEvents() {
    const seatOf = seatTimes();
    const limit = live ? Date.now() : end;
    return sorted
      .map(({ id, type, pid, t }) => {
        const floor = type === "buyin" ? start : Math.max(start, seatOf[pid] ?? start);
        return { id, type, pid, t: Math.min(Math.max(t, floor), Math.max(limit, floor)) };
      })
      .sort((a, b) => a.t - b.t);
  }

  const showError = (problem) => {
    setError(problem);
    contentRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
  };

  async function save() {
    const problem = validate();
    if (problem) { showError(problem); return; }
    setSaving(true);
    setError(null);
    try {
      await onSave({
        startedAt: start,
        endedAt: live ? null : end,
        events: clampedEvents(),
        reason: reason.trim(),
      });
    } catch (err) {
      showError({ message: err?.message || "Could not save the timeline. Try again." });
      setSaving(false);
    }
  }

  const invalid = (id) => error?.id === id;
  const lengthNote = live
    ? `Running for ${formatLength(Date.now() - start)}`
    : `${formatLength(end - start)} at the table`;

  return (
    <div className="profile-page-overlay tl-edit-overlay">
      <div className="profile-page">
        <div className="profile-page-topbar">
          <button className="profile-page-back" aria-label="Back" onClick={onClose}>
            <ArrowLeft size={20} />
          </button>
          <div className="tl-edit-title">
            <span className="profile-page-topbar-name">Edit timeline</span>
            <span className="tl-edit-subtitle">{live ? "Live game" : "Saved game"}</span>
          </div>
          <button className="btn tl-edit-save" onClick={save} disabled={saving || !dirty}>{saving ? "Saving…" : "Save"}</button>
        </div>
        <div className="profile-page-content tl-edit-content" ref={contentRef}>
          {error && <div className="tl-edit-error" role="alert">{error.message}</div>}

          <div className="breakdown-section-label">Game</div>
          <div className="tl-group">
            <div className="tl-row">
              <span className="tl-icon tl-icon--start" aria-hidden="true"><Play size={14} /></span>
              <label className="tl-row-label" htmlFor="tl-start">Started</label>
              <TimeChip id="tl-start" label="Start time" value={toClockInput(start)} onChange={changeStart} invalid={invalid("start")} />
            </div>
            {!live && (
              <div className="tl-row">
                <span className="tl-icon tl-icon--end" aria-hidden="true"><Flag size={14} /></span>
                <label className="tl-row-label" htmlFor="tl-end">Ended</label>
                <TimeChip id="tl-end" label="End time" value={toClockInput(end)} onChange={changeEnd} invalid={invalid("end")} />
              </div>
            )}
          </div>
          <p className="tl-footnote">{lengthNote}</p>

          <div className="breakdown-section-label">Players</div>
          <div className="tl-players">
            {players.map((pid) => {
              const name = getName(pid) || "Unknown player";
              const own = sorted.filter((r) => r.pid === pid);
              const rebuys = own.filter((r) => r.type === "rebuy").length;
              const lastMove = [...own].reverse().find((r) => r.type === "out" || r.type === "back");
              const isOut = lastMove?.type === "out";
              const rebuyText = rebuys ? `${rebuys} ${rebuys === 1 ? "rebuy" : "rebuys"}` : "";
              const summary = isOut ? ["Out", rebuyText].filter(Boolean).join(" · ") : (rebuyText || "No rebuys");
              return (
                <section key={pid} className="tl-group tl-player" aria-label={name}>
                  <header className="tl-player-head">
                    {renderAvatar && <span className="tl-player-avatar">{renderAvatar(pid, name)}</span>}
                    <span className="tl-player-id">
                      <span className="tl-player-name">{name}</span>
                      <span className="tl-player-summary">{summary}</span>
                    </span>
                    <div className="tl-player-actions">
                      <button type="button" className="tl-add tl-add--primary" aria-label={`Add a rebuy for ${name}`} onClick={() => addRow(pid, "rebuy")}>
                        <Plus size={15} aria-hidden="true" /> Rebuy
                      </button>
                      <button type="button" className="tl-add" aria-label={`Mark ${name} ${isOut ? "back in" : "out"}`} onClick={() => addRow(pid, isOut ? "back" : "out")}>
                        {isOut ? <LogIn size={15} aria-hidden="true" /> : <LogOut size={15} aria-hidden="true" />} {isOut ? "Back in" : "Out"}
                      </button>
                    </div>
                  </header>
                  {own.map((r) => {
                    const Icon = TYPE_ICONS[r.type] || Plus;
                    const label = TYPE_LABELS[r.type] || r.type;
                    const inputId = `tl-ev-${r.id}`;
                    return (
                      <div key={r.id} className={`tl-row${r.id === addedId ? " tl-row--new" : ""}`}>
                        <span className={`tl-icon tl-icon--${r.type}`} aria-hidden="true"><Icon size={14} /></span>
                        <label className="tl-row-label" htmlFor={inputId}>{label}</label>
                        <TimeChip
                          id={inputId}
                          label={`${getName(r.pid)} ${label} time`}
                          value={toClockInput(r.t)}
                          onChange={(value) => changeRow(r.id, value)}
                          invalid={invalid(r.id)}
                          inputRef={r.id === addedId ? addedRef : undefined}
                        />
                        {r.type === "buyin" ? (
                          <span className="tl-remove-spacer" />
                        ) : (
                          <button type="button" className="tl-remove" aria-label={`Remove ${getName(r.pid)} ${label}`} onClick={() => removeRow(r.id)}>
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </section>
              );
            })}
          </div>
          <p className="tl-footnote">A rebuy adds one buy-in to the player's total. Removing a rebuy takes one away.</p>

          <div className="breakdown-section-label">Reason <span className="tl-optional">optional</span></div>
          <textarea
            className="tl-reason"
            rows={2}
            maxLength={500}
            aria-label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="For example: forgot Sara's rebuy"
          />
          <p className="tl-footnote">Saved to the change log with your name. Everyone in the group can see it.</p>
        </div>
      </div>
    </div>
  );
});
